import { useEffect, useRef, useState } from 'react';
import { useToast } from '@/components/ui';
import { isApiError, useTranscribeAudio, type TranscriptionResult } from '@/lib/api';

/** Teto de uma gravação: passou disso, a gravação para sozinha e é transcrita. */
export const DICTATION_MAX_MS = 60_000;

/**
 * Formatos tentados, na ordem. Ogg e mp4 vêm antes do webm porque o provedor de
 * transcrição documenta os dois e não o webm; o Chrome só grava webm e cai nele,
 * o Safari só grava mp4.
 */
const PREFERRED_TYPES = ['audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];

export type DictationState = 'idle' | 'recording' | 'transcribing';

export interface Dictation {
  state: DictationState;
  /** O navegador grava áudio? Sem isso o botão nem aparece. */
  supported: boolean;
  /** Começa a gravar ou, gravando, para e transcreve. */
  toggle: () => void;
}

/**
 * De onde vem o texto do clipe quando não é a transcrição autenticada do console
 * (o widget anônimo do site tem rota e limites próprios).
 */
export interface TranscriptionSource {
  transcribe: (audio: Blob, mimetype: string) => Promise<TranscriptionResult>;
  /** A frase de uma recusa, por status HTTP, que esta origem diz do seu jeito. */
  failureCopy?: Partial<Record<number, string>>;
}

function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

function pickMimeType(): string | undefined {
  return PREFERRED_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

/** O texto ditado entra DEPOIS do que já estava escrito, nunca no lugar dele. */
export function appendDictated(current: string, dictated: string): string {
  const head = current.trimEnd();
  return head ? `${head} ${dictated}` : dictated;
}

/** O que dizer de uma transcrição que falhou: o servidor já fala a língua da pessoa. */
function transcriptionFailure(error: unknown, copy?: TranscriptionSource['failureCopy']): string {
  const own = isApiError(error) ? copy?.[error.status] : undefined;
  if (own) return own;
  if (isApiError(error) && error.status === 503) {
    return 'A transcrição está indisponível no momento. Digite a pergunta.';
  }
  if (isApiError(error) && error.status === 413) {
    return 'A gravação ficou grande demais. Grave um trecho mais curto.';
  }
  return isApiError(error) ? error.message : 'Não foi possível transcrever o áudio. Tente de novo.';
}

/**
 * O microfone de um compositor: grava com o `MediaRecorder`, manda o clipe para a
 * transcrição do servidor e entrega o texto a `onText` — quem chama decide onde
 * ele entra (os compositores ANEXAM ao que já foi escrito). Nada do áudio fica
 * guardado: os pedaços vivem só até o clipe ser enviado. Sem `source`, a
 * transcrição é a do console (`/v1/knowledge/transcribe`).
 */
export function useDictation(onText: (text: string) => void, source?: TranscriptionSource): Dictation {
  const [state, setState] = useState<DictationState>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  // Enquanto a permissão do microfone não volta o estado segue 'idle'; sem esta
  // trava, um segundo toque abriria uma gravação órfã, escondida até o teto.
  const startingRef = useRef(false);
  // O texto chega depois de uma ida ao servidor; o callback do render atual é o
  // que vale, não o do render em que a gravação começou.
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  // Pelo mesmo motivo, a origem lida é a do render atual.
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const consoleTranscription = useTranscribeAudio();
  const { toast } = useToast();

  const fail = (description: string): void => {
    toast({ tone: 'error', title: 'Microfone', description });
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
    };
  }, []);

  const finish = async (blob: Blob): Promise<void> => {
    if (!mountedRef.current) return;
    if (blob.size === 0) {
      setState('idle');
      return;
    }
    setState('transcribing');
    const current = sourceRef.current;
    try {
      const { text } = current
        ? await current.transcribe(blob, blob.type)
        : await consoleTranscription.mutateAsync({ audio: blob, mimetype: blob.type });
      if (!mountedRef.current) return;
      if (text.trim()) onTextRef.current(text.trim());
      else fail('Não ouvi nada na gravação. Tente de novo, mais perto do microfone.');
    } catch (error) {
      if (mountedRef.current) fail(transcriptionFailure(error, current?.failureCopy));
    } finally {
      if (mountedRef.current) setState('idle');
    }
  };

  const start = async (): Promise<void> => {
    let stream: MediaStream;
    startingRef.current = true;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      startingRef.current = false;
      const denied = error instanceof DOMException && error.name === 'NotAllowedError';
      fail(
        denied
          ? 'O acesso ao microfone foi negado. Libere-o nas configurações do navegador.'
          : 'Não foi possível acessar o microfone.',
      );
      return;
    }
    startingRef.current = false;
    if (!mountedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      recorderRef.current = null;
      stream.getTracks().forEach((track) => track.stop());
      void finish(new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }));
    };
    recorderRef.current = recorder;
    recorder.start();
    setState('recording');
    timerRef.current = setTimeout(() => recorder.stop(), DICTATION_MAX_MS);
  };

  const toggle = (): void => {
    if (state === 'transcribing' || startingRef.current) return;
    if (state === 'recording') {
      recorderRef.current?.stop();
      return;
    }
    void start();
  };

  return { state, supported: isSupported(), toggle };
}
